import React, { useEffect, useMemo, useState } from 'react';
import { WarningOutlined } from '@ant-design/icons';
import DraftPrepHeader from '../components/DraftPrepHeader';
import EmptyState from '../components/EmptyState';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  getPlayerTier,
  rankDraftRecommendations,
  sortPlayersByTier,
  useDraftRankings,
  useKeeperOptions,
} from '../hooks/useDraftPrep';
import { DraftRankingPlayer, DraftRecommendation, KeeperTeam, TierSource } from '../types/draftPrep';
import './DraftPrep.css';

const POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const formatOffset = (value: number) => `${value > 0 ? '+' : ''}${value.toFixed(1)}`;

const valueClass = (value: number) => {
  if (value >= 3) return 'is-strong-value';
  if (value >= 0.5) return 'is-value';
  if (value <= -3) return 'is-strong-fade';
  if (value <= -0.5) return 'is-fade';
  return 'is-neutral';
};

type PlayerDraftState = 'available' | 'kept-mine' | 'kept-other' | 'targeted' | 'drafted';

const PLAYER_STATE_CYCLE: PlayerDraftState[] = [
  'available',
  'targeted',
  'drafted',
  'kept-mine',
  'kept-other',
];

const stateLabel = (state: PlayerDraftState) => ({
  available: 'available',
  'kept-mine': 'kept by me',
  'kept-other': 'kept by someone else',
  targeted: 'targeted by me',
  drafted: 'drafted by someone else',
}[state]);

const trackingStorageKey = (season: number) => `fantasy-archive-draft-tracking-${season}`;

const loadTrackingStates = (season: number): Record<string, PlayerDraftState> => {
  try {
    const stored = window.localStorage.getItem(trackingStorageKey(season));
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const PlayerRow: React.FC<{
  player: DraftRankingPlayer;
  startsTier: boolean;
  boardRank: number;
  tier?: number;
  tierSource: TierSource;
  playerState: PlayerDraftState;
  keeperTeam?: string;
  onCyclePlayerState: () => void;
}> = ({ player, startsTier, boardRank, tier, tierSource, playerState, keeperTeam, onCyclePlayerState }) => (
  <div
    className={`ranking-row position-${player.position.toLowerCase()} ${startsTier ? 'starts-tier' : ''} is-${playerState}`}
    role="button"
    tabIndex={0}
    aria-label={`${player.name}: ${stateLabel(playerState)}. Click to change player state.`}
    onClick={onCyclePlayerState}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onCyclePlayerState();
      }
    }}
  >
    <div className="ranking-weighted" title="Board row number; keeper-adjusted positional rank shown below">
      <strong>{boardRank}</strong>
      <span>adj {player.weightedRank.toFixed(1)}</span>
    </div>
    <div className="ranking-player-copy">
      <div>
        <strong>{player.name}</strong>
        {!player.playerId && <WarningOutlined title="Player match needs review" />}
      </div>
      <span>
        {player.nflTeam || 'FA'} · {tierSource === 'redraft' ? 'std' : 'dyn'} tier {tier ?? '—'} · age {player.age ?? '—'} · bye {player.byeWeek ?? '—'}
        {(playerState === 'kept-mine' || playerState === 'kept-other') && keeperTeam && <> · keeper: {keeperTeam}</>}
      </span>
    </div>
    <div className="ranking-ranks" title="Redraft rank / dynasty rank">
      <span><b>R</b>{player.redraftRank}</span>
      <span><b>D</b>{player.dynastyRank ?? '—'}</span>
    </div>
    <div className={`ranking-value ${valueClass(player.dynastyOffset)}`} title="Positive values receive a keeper-horizon boost">
      {formatOffset(player.dynastyOffset)}
    </div>
  </div>
);

const PositionBoard: React.FC<{
  position: string;
  players: DraftRankingPlayer[];
  playerStates: Map<string, PlayerDraftState>;
  keeperTeamsByPlayer: Map<number, KeeperTeam>;
  tierSource: TierSource;
  onCyclePlayerState: (rankingPlayerId: string) => void;
}> = ({ position, players, playerStates, keeperTeamsByPlayer, tierSource, onCyclePlayerState }) => (
  <section className={`position-board position-${position.toLowerCase()}`}>
    <header>
      <div>
        <span>{position}</span>
        <strong>{players.length} ranked</strong>
      </div>
      <div className="position-board-legend"><span>{tierSource === 'redraft' ? 'STD' : 'DYN'}</span> tiers</div>
    </header>
    <div className="position-board-rows">
      {players.map((player, index) => {
        const keeperTeam = player.playerId ? keeperTeamsByPlayer.get(player.playerId) : undefined;
        return (
          <PlayerRow
            key={player.rankingPlayerId}
            player={player}
            boardRank={index + 1}
            tier={getPlayerTier(player, tierSource)}
            tierSource={tierSource}
            startsTier={index === 0 || getPlayerTier(players[index - 1], tierSource) !== getPlayerTier(player, tierSource)}
            playerState={playerStates.get(player.rankingPlayerId) || 'available'}
            keeperTeam={keeperTeam?.teamName}
            onCyclePlayerState={() => onCyclePlayerState(player.rankingPlayerId)}
          />
        );
      })}
    </div>
  </section>
);

const NextAvailablePlayers: React.FC<{
  recommendations: DraftRecommendation[];
  playerStates: Map<string, PlayerDraftState>;
  onCyclePlayerState: (rankingPlayerId: string) => void;
}> = ({ recommendations, playerStates, onCyclePlayerState }) => (
  <section className="next-available-board" aria-label="Next 15 available players">
    <header>
      <div>
        <span>Live draft queue</span>
        <h2>Next {recommendations.length} available</h2>
      </div>
      <small>Weighted rank + league scarcity + your open starter slots · keeper and drafted states are removed automatically</small>
    </header>
    <div className="next-player-list">
      {recommendations.map(({
        player,
        draftScore,
        fillsStarterSlot,
        rosterCount,
        starterSlots,
        tier,
        remainingInTier,
        isActiveTier,
        tierUrgencyBonus,
      }, index) => {
        const playerState = playerStates.get(player.rankingPlayerId) || 'available';
        return (
          <button
            type="button"
            key={player.rankingPlayerId}
            className={`next-player position-${player.position.toLowerCase()} is-${playerState}`}
            aria-label={`${player.name}: ${stateLabel(playerState)}. Click to change player state.`}
            title={`${fillsStarterSlot ? `Fills an open starter slot (${rosterCount}/${starterSlots})` : 'Bench/depth option'} · ${isActiveTier ? `active tier with ${remainingInTier} left · tier urgency +${tierUrgencyBonus.toFixed(1)} · ` : ''}draft fit ${draftScore.toFixed(1)}`}
            onClick={() => onCyclePlayerState(player.rankingPlayerId)}
          >
            <span className="next-player-number">{index + 1}</span>
            <span className="next-player-position">{player.position}</span>
            <span className="next-player-name">{player.name}</span>
            <span className={`next-player-rank ${isActiveTier ? 'is-active-tier' : ''}`}>
              <b>{tier == null ? '—' : `T${tier}`}</b>
              <small>{isActiveTier ? `${remainingInTier} left` : (fillsStarterSlot ? `${rosterCount}/${starterSlots}` : 'bench')}</small>
            </span>
          </button>
        );
      })}
    </div>
  </section>
);

const DraftRankings: React.FC = () => {
  const [season, setSeason] = useState(new Date().getFullYear());
  const [dynastyWeight, setDynastyWeight] = useState(0.25);
  const [tierSource, setTierSource] = useState<TierSource>('redraft');
  const [currentTeamId, setCurrentTeamId] = useState('');
  const [playerStateOverrides, setPlayerStateOverrides] = useState<Record<string, PlayerDraftState>>(
    () => loadTrackingStates(new Date().getFullYear()),
  );
  const rankings = useDraftRankings(season, dynastyWeight);
  const keepers = useKeeperOptions(season, dynastyWeight);
  const keeperTeams = useMemo(() => keepers.data?.teams || [], [keepers.data?.teams]);
  const defaultTeam = keeperTeams.find((team) => team.teamName.toLowerCase().includes('mojos')) || keeperTeams[0];
  const activeTeamId = keeperTeams.some((team) => team.teamId === currentTeamId)
    ? currentTeamId
    : defaultTeam?.teamId || '';
  const keeperTeamsByPlayer = useMemo(() => {
    const result = new Map<number, KeeperTeam>();
    keeperTeams.forEach((team) => team.players
      .filter((player) => player.isLikelyKeeper)
      .forEach((player) => result.set(player.playerId, team)));
    return result;
  }, [keeperTeams]);
  const keeperPositionsByPlayer = useMemo(() => {
    const result = new Map<number, string>();
    keeperTeams.forEach((team) => team.players
      .filter((player) => player.isLikelyKeeper)
      .forEach((player) => result.set(player.playerId, player.position)));
    return result;
  }, [keeperTeams]);
  const projectedPlayerStates = useMemo(() => {
    const result = new Map<string, PlayerDraftState>();
    (rankings.data?.players || []).forEach((player) => {
      const keeperTeam = player.playerId ? keeperTeamsByPlayer.get(player.playerId) : undefined;
      result.set(
        player.rankingPlayerId,
        keeperTeam ? (keeperTeam.teamId === activeTeamId ? 'kept-mine' : 'kept-other') : 'available',
      );
    });
    return result;
  }, [activeTeamId, keeperTeamsByPlayer, rankings.data?.players]);
  const playerStates = useMemo(() => {
    const result = new Map(projectedPlayerStates);
    Object.entries(playerStateOverrides).forEach(([rankingPlayerId, state]) => result.set(rankingPlayerId, state));
    return result;
  }, [playerStateOverrides, projectedPlayerStates]);
  const byPosition = useMemo(() => Object.fromEntries(POSITIONS.map((position) => [
    position,
    sortPlayersByTier(
      (rankings.data?.players || []).filter((player) => player.position === position),
      tierSource,
    ),
  ])), [rankings.data, tierSource]);
  const currentRosterPositions = useMemo(() => (rankings.data?.players || [])
    .filter((player) => playerStates.get(player.rankingPlayerId) === 'kept-mine')
    .map((player) => player.playerId ? keeperPositionsByPlayer.get(player.playerId) || player.position : player.position),
  [keeperPositionsByPlayer, playerStates, rankings.data?.players]);
  const nextAvailablePlayers = useMemo(() => rankDraftRecommendations(
    (rankings.data?.players || []).filter((player) => {
      const state = playerStates.get(player.rankingPlayerId) || 'available';
      return state === 'available' || state === 'targeted';
    }),
    currentRosterPositions,
    15,
    tierSource,
  ), [currentRosterPositions, playerStates, rankings.data?.players, tierSource]);
  const cyclePlayerState = (rankingPlayerId: string) => {
    setPlayerStateOverrides((current) => {
      const currentState = current[rankingPlayerId]
        || projectedPlayerStates.get(rankingPlayerId)
        || 'available';
      const nextState = PLAYER_STATE_CYCLE[(PLAYER_STATE_CYCLE.indexOf(currentState) + 1) % PLAYER_STATE_CYCLE.length];
      return { ...current, [rankingPlayerId]: nextState };
    });
  };
  const changeSeason = (nextSeason: number) => {
    setSeason(nextSeason);
    setPlayerStateOverrides(loadTrackingStates(nextSeason));
  };

  useEffect(() => {
    try {
      window.localStorage.setItem(trackingStorageKey(season), JSON.stringify(playerStateOverrides));
    } catch {
      // Draft tracking still works for the current tab when browser storage is unavailable.
    }
  }, [playerStateOverrides, season]);

  return (
    <div className="draft-prep-page">
      <DraftPrepHeader
        title={`${season} master draft rankings`}
        description="Standard-scoring consensus remains the anchor. Dynasty rank supplies a controlled keeper offset, while tiers show where waiting actually costs value."
        season={season}
        dynastyWeight={dynastyWeight}
        onSeasonChange={changeSeason}
        onDynastyWeightChange={setDynastyWeight}
        teams={keeperTeams.map((team) => ({ teamId: team.teamId, teamName: team.teamName }))}
        currentTeamId={activeTeamId}
        onCurrentTeamChange={setCurrentTeamId}
      />

      {rankings.isLoading && <LoadingSpinner size="large" message="Loading exported ranking snapshots…" />}
      {rankings.error && (
        <EmptyState
          message="Draft rankings are unavailable"
          description={`${rankings.error.message}. Import the season's CSV files locally, then run the draft-prep export.`}
          icon="📊"
        />
      )}
      {rankings.data && rankings.data.players.length === 0 && (
        <EmptyState
          message={`No ${season} rankings imported`}
          description="Import all eight positional CSV files locally and regenerate the static draft-prep export."
          icon="📥"
        />
      )}
      {rankings.data && rankings.data.players.length > 0 && (
        <>
          <div className="ranking-summary-strip">
            <span><strong>{rankings.data.players.length}</strong> redraft-ranked players</span>
            <span><strong>{rankings.data.snapshots.length}</strong> active snapshots</span>
            <span className={rankings.data.unmatchedRows ? 'has-warning' : ''}>
              <strong>{rankings.data.unmatchedRows}</strong> unmatched rows
            </span>
            <span>Value = redraft rank − weighted rank</span>
            <div className="tier-source-control" role="group" aria-label="Tier source">
              <strong>Tier source</strong>
              <button
                type="button"
                className={tierSource === 'redraft' ? 'active' : ''}
                aria-pressed={tierSource === 'redraft'}
                onClick={() => setTierSource('redraft')}
              >
                Standard
              </button>
              <button
                type="button"
                className={tierSource === 'dynasty' ? 'active' : ''}
                aria-pressed={tierSource === 'dynasty'}
                onClick={() => setTierSource('dynasty')}
              >
                Dynasty
              </button>
            </div>
            <span className="keeper-legend is-kept-mine"><strong>Green</strong> kept by me</span>
            <span className="keeper-legend is-kept-other"><strong>Red</strong> kept by someone else</span>
            <span className="draft-tracking-legend is-targeted"><strong>Light green</strong> targeted by me</span>
            <span className="draft-tracking-legend is-drafted"><strong>Click twice</strong> drafted</span>
            <span>Click cycle: available → target → drafted → mine → other</span>
            <button
              type="button"
              className="draft-tracking-reset"
              disabled={Object.keys(playerStateOverrides).length === 0}
              onClick={() => setPlayerStateOverrides({})}
            >
              Reset all player states
            </button>
          </div>
          <NextAvailablePlayers
            recommendations={nextAvailablePlayers}
            playerStates={playerStates}
            onCyclePlayerState={cyclePlayerState}
          />
          <div className="position-board-grid">
            {POSITIONS.map((position) => (
              <PositionBoard
                key={position}
                position={position}
                players={byPosition[position]}
                keeperTeamsByPlayer={keeperTeamsByPlayer}
                playerStates={playerStates}
                tierSource={tierSource}
                onCyclePlayerState={cyclePlayerState}
              />
            ))}
          </div>
          <footer className="draft-prep-source-note">
            Generated from dated ranking snapshots and the prior season's closing rosters. Redraft rank uses standard
            scoring; ADP is intentionally ignored. The page makes no live database or draft-prep API requests.
          </footer>
        </>
      )}
    </div>
  );
};

export default DraftRankings;
